import { Badge, Flex, Select, Switch, Typography, Button } from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import { NIVEL } from "@/utilities/niveles-de-permiso";

const { Text } = Typography;
const { Option } = Select;

const defaultOptions = [
  { value: "admin", label: NIVEL.admin },
  { value: "editor", label: NIVEL.editor },
  { value: "viewer", label: "Lector" },
  { value: "no_access", label: NIVEL.no_access },
];

const customerOptions = [
  { value: "remove", label: "Remove" },
];

const removeOption = { value: "remove", label: "Remove" };

export const PermissionSelectRow = ({
  label,
  value,
  strong,
  badge,
  isLoadingChange,
  onChange,
  saveChanges,
  customerMode,
  allowRemove,
  asSwitch,
  disabled,
}: {
  label: React.ReactNode;
  value?: string;
  strong?: boolean;
  badge?: boolean;
  isLoadingChange?: boolean;
  onChange: (value: string) => void;
  saveChanges?: () => Promise<void>;
  customerMode?: boolean;
  /** Append a "Remove" option to the non-customer (seller/store) options. */
  allowRemove?: boolean;
  /**
   * Render as a boolean switch instead of a select. Used for `order_payment`
   * and `order_shipment` sub-permissions, matching the store-operator config
   * panel UX.
   *
   * Mapping:
   *   - checked  → value in {admin, editor}; toggle ON emits `editor`.
   *   - !checked → value in {viewer, no_access, undefined}; toggle OFF emits
   *                `no_access`.
   */
  asSwitch?: boolean;
  /**
   * Solo lectura: no deja cambiar el valor desde acá.
   *
   * Hoy NINGÚN caller lo usa. Lo usaba el sidebar de participantes del pedido, con el criterio de
   * que el nivel de un operador se editaba únicamente en su panel de configuración
   * (`/stores/operators/...` o `/stores/linked-sellers/...`). Eso se revirtió: el nivel se elige y
   * se edita por participación, en el pedido. Se deja el prop porque es genérico y sigue siendo
   * útil para un caso de solo-lectura, pero si nadie lo usa conviene borrarlo.
   */
  disabled?: boolean;
}) => {
  if (asSwitch) {
    const checked = value === "admin" || value === "editor";
    return (
      <Flex
        align="center"
        justify="space-between"
        style={{ padding: "4px 0" }}
      >
        <Flex align="center" gap={6}>
          {badge && <Badge status="default" />}
          <Text strong={strong} style={{ fontSize: "12px" }}>
            {label}
          </Text>
        </Flex>
        <Switch
          size="small"
          checked={checked}
          loading={isLoadingChange}
          disabled={isLoadingChange || disabled}
          onChange={(c) => onChange(c ? "editor" : "no_access")}
        />
      </Flex>
    );
  }

  if (customerMode) {
    return (
      <Flex align="center" justify="space-between" style={{ padding: "4px 0" }}>
        <Flex align="center" gap={6}>
          {badge && <Badge status="default" />}
          <Text strong={strong} style={{ fontSize: "12px" }}>{label}</Text>
        </Flex>
        <Button
          type="text"
          size="small"
          loading={isLoadingChange}
          disabled={isLoadingChange || disabled}
          icon={<DeleteOutlined style={{ color: "black" }} />}
          onClick={() => onChange("remove")}
        />
      </Flex>
    );
  }

  const options = allowRemove
    ? [...defaultOptions, removeOption]
    : defaultOptions;

  return (
    <Flex align="center" justify="space-between" style={{ padding: "4px 0" }}>
      <Flex align="center" gap={6}>
        {badge && <Badge status="default" />}
        <Text strong={strong} style={{ fontSize: "12px" }}>{label}</Text>
      </Flex>
      <Select
        loading={isLoadingChange}
        disabled={isLoadingChange || disabled}
        size="small"
        placeholder="Select"
        style={{ width: "130px" }}
        value={value}
        onChange={(value) => onChange(value)}
      >
        {options.map((option) => (
          <Option key={option.value} value={option.value}>
            {option.label}
          </Option>
        ))}
      </Select>
    </Flex>
  );
};
